import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { DashboardLayout } from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2, ArrowLeft, FileText, Upload } from "lucide-react";
import { ContractAnalyzer } from "@/components/deals/ContractAnalyzer";
import { BulkImportDialog } from "@/components/deals/BulkImportDialog";

const dealSchema = z.object({
  athlete_id: z.string().uuid("Please select an athlete"),
  title: z.string().min(3, "Title must be at least 3 characters").max(200),
  description: z.string().optional(),
  brand_name: z.string().min(2, "Brand name is required").max(200),
  brand_contact_email: z.string().email("Invalid email").optional().or(z.literal("")),
  brand_contact_phone: z.string().optional(),
  deal_type: z.enum(["endorsement", "appearance", "social_media", "autograph", "merchandise", "other"]),
  amount: z.number().min(0, "Amount must be positive"),
  start_date: z.string().min(1, "Start date is required"),
  end_date: z.string().optional(),
});

type DealFormData = z.infer<typeof dealSchema>;

const SubmitDeal = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedDealId, setSubmittedDealId] = useState<string | null>(null);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();

  const { data: athletes } = useQuery({
    queryKey: ["athletes"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("athletes" as any)
        .select("id, name, sport")
        .order("name");
      
      if (error) throw error;
      return data as any[];
    },
  });

  const { register, handleSubmit, formState: { errors }, setValue, watch } = useForm<DealFormData>({
    resolver: zodResolver(dealSchema),
  });

  const onSubmit = async (data: DealFormData) => {
    setIsSubmitting(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      // Fetch high-value threshold setting
      const { data: settings } = await supabase
        .from("institution_settings" as any)
        .select("high_value_threshold, auto_analyze_enabled")
        .limit(1)
        .maybeSingle();

      const highValueThreshold = (settings as any)?.high_value_threshold || 10000;
      const isHighValue = data.amount >= highValueThreshold;
      
      const { data: dealData, error } = await supabase
        .from("deals" as any)
        .insert([{
          ...data,
          status: "pending_compliance",
          submitted_by: user?.id,
        }] as any)
        .select()
        .single();

      if (error) throw error;
      
      if (dealData) {
        setSubmittedDealId((dealData as any).id);
      }

      // Show appropriate toast based on deal value
      if (isHighValue && (settings as any)?.auto_analyze_enabled !== false) {
        toast({
          title: "High-Value Deal Submitted",
          description: `Deal submitted for compliance review. AI analysis will be completed automatically within a few seconds.`,
          duration: 6000,
        });
      } else {
        toast({
          title: "Success",
          description: "Deal submitted for compliance review",
        });
      }
      
      navigate("/dashboard/deals");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div>
          <div className="flex justify-between items-center mb-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/dashboard/deals")}
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Deals
            </Button>
            <Button
              variant="outline"
              onClick={() => setIsBulkImportOpen(true)}
            >
              <Upload className="mr-2 h-4 w-4" />
              Bulk Import Instead
            </Button>
          </div>

          <h1 className="text-3xl font-bold">Submit NIL Deal</h1>
          <p className="text-muted-foreground mt-2">
            Submit a new NIL deal for compliance review
          </p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Deal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="athlete_id">Athlete *</Label>
                <Select onValueChange={(value) => setValue("athlete_id", value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select athlete" />
                  </SelectTrigger>
                  <SelectContent>
                    {athletes?.map((athlete) => (
                      <SelectItem key={athlete.id} value={athlete.id}>
                        {athlete.name} - {athlete.sport}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.athlete_id && (
                  <p className="text-sm text-destructive">{errors.athlete_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="title">Deal Title *</Label>
                <Input
                  id="title"
                  {...register("title")}
                  placeholder="e.g., Social Media Endorsement - Nike"
                />
                {errors.title && (
                  <p className="text-sm text-destructive">{errors.title.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  {...register("description")}
                  placeholder="Describe the deal and deliverables..."
                  rows={4}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand_name">Brand/Company Name *</Label>
                  <Input
                    id="brand_name"
                    {...register("brand_name")}
                    placeholder="Nike"
                  />
                  {errors.brand_name && (
                    <p className="text-sm text-destructive">{errors.brand_name.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deal_type">Deal Type *</Label>
                  <Select onValueChange={(value) => setValue("deal_type", value as any)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="endorsement">Endorsement</SelectItem>
                      <SelectItem value="appearance">Appearance</SelectItem>
                      <SelectItem value="social_media">Social Media</SelectItem>
                      <SelectItem value="autograph">Autograph Session</SelectItem>
                      <SelectItem value="merchandise">Merchandise</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.deal_type && (
                    <p className="text-sm text-destructive">{errors.deal_type.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="brand_contact_email">Brand Contact Email</Label>
                  <Input
                    id="brand_contact_email"
                    type="email"
                    {...register("brand_contact_email")}
                    placeholder="contact@brand.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="brand_contact_phone">Brand Contact Phone</Label>
                  <Input
                    id="brand_contact_phone"
                    {...register("brand_contact_phone")}
                    placeholder="(555) 123-4567"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="amount">Deal Amount (USD) *</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  {...register("amount", { valueAsNumber: true })}
                  placeholder="1000.00"
                />
                {errors.amount && (
                  <p className="text-sm text-destructive">{errors.amount.message}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="start_date">Start Date *</Label>
                  <Input
                    id="start_date"
                    type="date"
                    {...register("start_date")}
                  />
                  {errors.start_date && (
                    <p className="text-sm text-destructive">{errors.start_date.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="end_date">End Date</Label>
                  <Input
                    id="end_date"
                    type="date"
                    {...register("end_date")}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/dashboard/deals")}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    "Submit for Review"
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {submittedDealId && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Optional: Upload Contract for AI Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ContractAnalyzer dealId={submittedDealId} />
            </CardContent>
          </Card>
        )}
      </div>

      <BulkImportDialog
        open={isBulkImportOpen}
        onOpenChange={setIsBulkImportOpen}
      />
    </DashboardLayout>
  );
};

export default SubmitDeal;